import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  CaretRight,
  DownloadSimple,
  EnvelopeSimple,
  MagnifyingGlass,
  Moon,
  Phone,
  Sun,
} from '@phosphor-icons/react';
import type {
  AboutValues,
  ContactValues,
  EducationValues,
  ExperienceValues,
  HeroValues,
  ProjectsValues,
} from '../../schemas';
import { PLATFORM_META } from '../../linkPlatforms';
import type { PortfolioSectionKind } from '@/types/portfolio';
import type { TemplateProps } from './shared';

interface SectionMeta {
  readonly key: PortfolioSectionKind;
  readonly label: string;
  readonly anchor: string;
}

const SECTION_ORDER: ReadonlyArray<SectionMeta> = [
  { key: 'hero', label: 'Introduction', anchor: '#introduction' },
  { key: 'about', label: 'About Me', anchor: '#about' },
  { key: 'projects', label: 'Projects', anchor: '#projects' },
  { key: 'experience', label: 'Experience', anchor: '#experience' },
  { key: 'education', label: 'Education', anchor: '#education' },
  { key: 'contact', label: 'Contact', anchor: '#contact' },
];

export function CompassTemplate({ draft, enabled, ownerName }: TemplateProps) {
  const visible = SECTION_ORDER.filter((s) => enabled.has(s.key) && draft[s.key]);
  const quickLinks = draft.contact?.links?.slice(0, 2) ?? [];
  const resumeUrl = draft.about?.resumeUrl;

  const [dark, setDark] = useState(false);
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>(visible[0]?.anchor.slice(1) ?? '');
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const filteredSections = useMemo(() => {
    if (!query.trim()) return visible;
    const q = query.trim().toLowerCase();
    return visible.filter((s) => s.label.toLowerCase().includes(q));
  }, [visible, query]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
        setTimeout(() => searchRef.current?.focus(), 0);
      }
      if (e.key === 'Escape') setSearchOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const sections = Array.from(root.querySelectorAll<HTMLElement>('section[id]'));
    if (!sections.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries.filter((e) => e.isIntersecting);
        if (visibleEntries.length) {
          const top = visibleEntries.sort(
            (a, b) => a.boundingClientRect.top - b.boundingClientRect.top,
          )[0];
          setActiveSection(top.target.id);
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: [0, 0.1, 0.5] },
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [visible.length]);

  function jumpTo(anchor: string) {
    const el = rootRef.current?.querySelector(anchor);
    if (el instanceof HTMLElement) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setSearchOpen(false);
      setQuery('');
    }
  }

  const palette = dark
    ? {
        shell: 'bg-[#0B0B0F] text-[#E6E6EA]',
        headerBg: 'bg-[#0B0B0F]/85 border-[#1F1F26]',
        mutedText: 'text-[#9A9AA8]',
        faintText: 'text-[#6E6E7A]',
        cardBg: 'bg-[#14141B]',
        cardBorder: 'border-[#22222B]',
        rail: 'bg-[#1B1B24]',
        railHover: 'hover:bg-[#1B1B24]/60',
        kbdBg: 'bg-[#14141B] border-[#22222B] text-[#9A9AA8]',
        accent: 'bg-violet-500',
        accentHover: 'hover:bg-violet-400',
      }
    : {
        shell: 'bg-canvas text-ink',
        headerBg: 'bg-canvas/85 border-line',
        mutedText: 'text-ink-muted',
        faintText: 'text-ink-faint',
        cardBg: 'bg-board',
        cardBorder: 'border-line',
        rail: 'bg-rail',
        railHover: 'hover:bg-rail/60',
        kbdBg: 'bg-canvas border-line text-ink-muted',
        accent: 'bg-violet-600',
        accentHover: 'hover:bg-violet-700',
      };

  const ctx: CompassCtx = { palette, dark };

  return (
    <div ref={rootRef} className={`relative min-h-full font-sans ${palette.shell}`}>
      <header className={`sticky top-0 z-10 border-b backdrop-blur ${palette.headerBg}`}>
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-6 py-3">
          <div className="flex items-center gap-5">
            <a
              href="#introduction"
              onClick={(e) => {
                e.preventDefault();
                jumpTo('#introduction');
              }}
              className="flex items-center gap-1.5 text-sm font-semibold tracking-tight"
            >
              <ArrowUpRight size={14} weight="bold" className="-rotate-12" />
              {ownerName}
            </a>
            <nav className={`hidden items-center gap-4 text-sm sm:flex ${palette.mutedText}`}>
              <a
                href="#introduction"
                onClick={(e) => {
                  e.preventDefault();
                  jumpTo('#introduction');
                }}
                className={`font-medium ${dark ? 'text-white' : 'text-ink'}`}
              >
                Home
              </a>
              {quickLinks.map((l) => {
                const meta = PLATFORM_META[l.platform] ?? PLATFORM_META.custom;
                return (
                  <a
                    key={l.id}
                    href={l.href}
                    target="_blank"
                    rel="noreferrer"
                    className={`inline-flex items-center gap-1 ${dark ? 'hover:text-white' : 'hover:text-ink'}`}
                  >
                    {meta.label}
                    <ArrowUpRight size={10} weight="bold" />
                  </a>
                );
              })}
              {resumeUrl ? (
                <a
                  href={resumeUrl}
                  target="_blank"
                  rel="noreferrer"
                  className={`inline-flex items-center gap-1 ${dark ? 'hover:text-white' : 'hover:text-ink'}`}
                >
                  Resume
                  <ArrowUpRight size={10} weight="bold" />
                </a>
              ) : null}
            </nav>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setSearchOpen(true);
                setTimeout(() => searchRef.current?.focus(), 0);
              }}
              className={`hidden items-center gap-2 rounded-lg border px-3 py-1.5 text-xs md:flex ${palette.cardBg} ${palette.cardBorder} ${palette.faintText}`}
            >
              <MagnifyingGlass size={12} />
              <span>Search sections…</span>
              <kbd className={`ml-6 rounded border px-1 py-[1px] font-mono text-[10px] ${palette.kbdBg}`}>
                ⌘ K
              </kbd>
            </button>
            <div
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[11px] ${palette.cardBg} ${palette.cardBorder} ${palette.mutedText}`}
            >
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
              <LiveClock />
            </div>
            <button
              type="button"
              aria-label="Toggle theme"
              onClick={() => setDark((d) => !d)}
              className={`rounded-md border p-1.5 transition-colors ${palette.cardBg} ${palette.cardBorder} ${palette.mutedText}`}
            >
              {dark ? <Sun size={14} /> : <Moon size={14} />}
            </button>
          </div>
        </div>
      </header>

      {searchOpen ? (
        <SearchPalette
          ctx={ctx}
          query={query}
          setQuery={setQuery}
          inputRef={searchRef}
          sections={filteredSections}
          onPick={jumpTo}
          onClose={() => setSearchOpen(false)}
        />
      ) : null}

      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-6 py-10 lg:grid-cols-[200px_1fr]">
        <aside className="hidden lg:block">
          <div className="sticky top-20">
            <h4 className="mb-3 text-sm font-semibold tracking-tight">Sections</h4>
            <ul className="flex flex-col gap-0.5">
              {visible.map((s) => {
                const isActive = activeSection === s.anchor.slice(1);
                return (
                  <li key={s.key}>
                    <a
                      href={s.anchor}
                      onClick={(e) => {
                        e.preventDefault();
                        jumpTo(s.anchor);
                      }}
                      className={`block rounded-md px-3 py-2 text-sm transition-colors ${
                        isActive
                          ? `${palette.rail} ${dark ? 'text-white' : 'text-ink'}`
                          : `${palette.mutedText} ${palette.railHover} ${dark ? 'hover:text-white' : 'hover:text-ink'}`
                      }`}
                    >
                      {s.label}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>

        <main className="min-w-0">
          {visible.map((s, i) => {
            const prev = visible[i - 1];
            const next = visible[i + 1];
            const body = (() => {
              switch (s.key) {
                case 'hero':
                  return (
                    <HeroBlock
                      values={draft.hero!}
                      resumeUrl={resumeUrl}
                      contact={draft.contact}
                      ctx={ctx}
                    />
                  );
                case 'about':
                  return <AboutBlock values={draft.about!} ctx={ctx} />;
                case 'projects':
                  return <ProjectsBlock values={draft.projects!} ctx={ctx} />;
                case 'experience':
                  return <ExperienceBlock values={draft.experience!} ctx={ctx} />;
                case 'education':
                  return <EducationBlock values={draft.education!} ctx={ctx} />;
                case 'contact':
                  return <ContactBlock values={draft.contact!} ctx={ctx} />;
              }
            })();
            return (
              <section
                key={s.key}
                id={s.anchor.slice(1)}
                className={i === 0 ? 'pb-20' : `border-t ${palette.cardBorder} pb-20 pt-14`}
              >
                {body}
                <SectionPager prev={prev} next={next} ctx={ctx} onJump={jumpTo} />
              </section>
            );
          })}
        </main>
      </div>

      <footer className={`border-t px-6 py-8 text-center text-xs ${palette.cardBorder} ${palette.mutedText}`}>
        <p>
          ©{new Date().getFullYear()} · <span className={palette.faintText}>Built with</span>{' '}
          <span className={`font-medium ${dark ? 'text-white' : 'text-ink'}`}>Portfoli</span>
        </p>
        <p className={`mt-1 ${palette.faintText}`}>Developed by {ownerName}</p>
      </footer>
    </div>
  );
}

interface Palette {
  shell: string;
  headerBg: string;
  mutedText: string;
  faintText: string;
  cardBg: string;
  cardBorder: string;
  rail: string;
  railHover: string;
  kbdBg: string;
  accent: string;
  accentHover: string;
}

interface CompassCtx {
  palette: Palette;
  dark: boolean;
}

function LiveClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  const ss = String(now.getSeconds()).padStart(2, '0');
  return (
    <span>
      {hh}:{mm}:{ss}
    </span>
  );
}

function SearchPalette({
  ctx,
  query,
  setQuery,
  inputRef,
  sections,
  onPick,
  onClose,
}: {
  ctx: CompassCtx;
  query: string;
  setQuery: (v: string) => void;
  inputRef: React.RefObject<HTMLInputElement>;
  sections: ReadonlyArray<SectionMeta>;
  onPick: (anchor: string) => void;
  onClose: () => void;
}) {
  const { palette, dark } = ctx;
  return (
    <div
      className={`fixed inset-0 z-30 flex items-start justify-center px-4 pt-24 ${dark ? 'bg-black/60' : 'bg-ink/30'} backdrop-blur-sm`}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`w-full max-w-md overflow-hidden rounded-xl border shadow-lg ${palette.cardBg} ${palette.cardBorder}`}
      >
        <label
          className={`flex items-center gap-2 border-b px-4 py-3 text-sm ${palette.cardBorder} ${palette.mutedText}`}
        >
          <MagnifyingGlass size={14} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search sections…"
            className={`flex-1 bg-transparent outline-none ${dark ? 'text-white placeholder:text-[#6E6E7A]' : 'text-ink placeholder:text-ink-faint'}`}
          />
          <kbd className={`rounded border px-1 py-[1px] font-mono text-[10px] ${palette.kbdBg}`}>
            esc
          </kbd>
        </label>
        <ul className="max-h-72 overflow-auto py-1">
          {sections.length === 0 ? (
            <li className={`px-4 py-6 text-center text-xs ${palette.faintText}`}>
              No sections match "{query}".
            </li>
          ) : (
            sections.map((s) => (
              <li key={s.key}>
                <button
                  type="button"
                  onClick={() => onPick(s.anchor)}
                  className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm transition-colors ${palette.railHover} ${dark ? 'text-[#E6E6EA]' : 'text-ink'}`}
                >
                  <span>{s.label}</span>
                  <CaretRight size={12} weight="bold" className={palette.faintText} />
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}

function SectionPager({
  prev,
  next,
  ctx,
  onJump,
}: {
  prev?: SectionMeta;
  next?: SectionMeta;
  ctx: CompassCtx;
  onJump: (anchor: string) => void;
}) {
  const { palette, dark } = ctx;
  if (!prev && !next) return null;
  return (
    <div className="mt-14 flex items-center justify-between">
      {prev ? (
        <a
          href={prev.anchor}
          onClick={(e) => {
            e.preventDefault();
            onJump(prev.anchor);
          }}
          className={`inline-flex items-center gap-1.5 text-sm font-medium ${palette.mutedText} ${dark ? 'hover:text-white' : 'hover:text-ink'}`}
        >
          <ArrowLeft size={12} weight="bold" />
          {prev.label}
        </a>
      ) : (
        <span />
      )}
      {next ? (
        <a
          href={next.anchor}
          onClick={(e) => {
            e.preventDefault();
            onJump(next.anchor);
          }}
          className={`inline-flex items-center gap-1.5 text-sm font-medium ${dark ? 'text-white' : 'text-ink'}`}
        >
          {next.label}
          <ArrowRight size={12} weight="bold" />
        </a>
      ) : (
        <span />
      )}
    </div>
  );
}

function SectionTitle({ label, title, ctx }: { label: string; title: string; ctx: CompassCtx }) {
  const { palette, dark } = ctx;
  return (
    <div className="mb-6">
      <h2 className={`font-display text-4xl font-bold tracking-tight ${dark ? 'text-white' : 'text-ink'}`}>
        {label}
      </h2>
      <p
        className={`mt-2 font-display text-2xl font-semibold leading-tight tracking-tight ${palette.mutedText}`}
      >
        {title}
      </p>
    </div>
  );
}

function HeroBlock({
  values,
  resumeUrl,
  contact,
  ctx,
}: {
  values: HeroValues;
  resumeUrl?: string;
  contact?: ContactValues;
  ctx: CompassCtx;
}) {
  const { palette, dark } = ctx;
  const email = contact?.email;
  return (
    <div>
      <h1 className={`font-display text-5xl font-bold leading-[1.05] tracking-tight ${dark ? 'text-white' : 'text-ink'}`}>
        {values.headline}
      </h1>
      {values.subheadline ? (
        <h2 className={`mt-4 font-display text-2xl font-semibold leading-tight tracking-tight ${palette.mutedText}`}>
          {values.subheadline}
        </h2>
      ) : null}
      {values.tagline ? (
        <p className={`mt-6 max-w-[70ch] text-base leading-relaxed ${palette.mutedText}`}>
          {values.tagline}
        </p>
      ) : null}
      <div className="mt-8 flex flex-wrap items-center gap-3">
        {resumeUrl ? (
          <a
            href={resumeUrl}
            target="_blank"
            rel="noreferrer"
            className={`inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold text-white transition-colors ${palette.accent} ${palette.accentHover}`}
          >
            Get Resume <ArrowUpRight size={12} weight="bold" />
          </a>
        ) : null}
        {email ? (
          <a
            href={`mailto:${email}`}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold transition-colors ${dark ? 'text-white hover:bg-[#1B1B24]' : 'text-ink hover:bg-rail'}`}
          >
            <EnvelopeSimple size={14} weight="regular" /> Send Mail
          </a>
        ) : null}
        {values.ctaLabel && values.ctaHref ? (
          <a
            href={values.ctaHref}
            target="_blank"
            rel="noreferrer"
            className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${palette.cardBg} ${palette.cardBorder} ${dark ? 'text-white hover:bg-[#1B1B24]' : 'text-ink hover:bg-rail'}`}
          >
            {values.ctaLabel}
            <ArrowUpRight size={12} weight="bold" />
          </a>
        ) : null}
      </div>
    </div>
  );
}

function AboutBlock({ values, ctx }: { values: AboutValues; ctx: CompassCtx }) {
  const { palette, dark } = ctx;
  return (
    <div>
      <SectionTitle label="About Me" title="A quick look at who I am." ctx={ctx} />
      <p className={`max-w-[70ch] whitespace-pre-line text-base leading-relaxed ${palette.mutedText}`}>
        {values.body}
      </p>
      {values.skills.length ? (
        <div className="mt-8">
          <p className={`mb-3 text-sm font-semibold ${dark ? 'text-white' : 'text-ink'}`}>
            Skills &amp; Tools
          </p>
          <div className="flex flex-wrap gap-2">
            {values.skills.map((s) => (
              <span
                key={s}
                className={`rounded-md border px-2.5 py-1 text-xs font-medium ${palette.cardBg} ${palette.cardBorder} ${dark ? 'text-white' : 'text-ink'}`}
              >
                {s}
              </span>
            ))}
          </div>
        </div>
      ) : null}
      {values.resumeUrl ? (
        <a
          href={values.resumeUrl}
          target="_blank"
          rel="noreferrer"
          className={`mt-6 inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold text-white ${palette.accent} ${palette.accentHover}`}
        >
          <DownloadSimple size={12} weight="bold" /> Download résumé
        </a>
      ) : null}
    </div>
  );
}

function ProjectsBlock({ values, ctx }: { values: ProjectsValues; ctx: CompassCtx }) {
  const { palette, dark } = ctx;
  if (!values.items.length) return null;
  return (
    <div>
      <SectionTitle
        label="Projects"
        title="A lot of ideas, some are still under construction."
        ctx={ctx}
      />
      <div className="space-y-6">
        {values.items.map((p) => (
          <article
            key={p.id}
            className={`flex flex-col rounded-xl border p-6 transition-colors ${palette.cardBg} ${palette.cardBorder} ${dark ? 'hover:border-white/30' : 'hover:border-ink/30'}`}
          >
            {p.mediaKind !== 'none' && p.mediaUrl ? (
              <div className={`mb-4 -mx-6 -mt-6 overflow-hidden rounded-t-xl border-b ${palette.cardBorder} ${palette.shell}`}>
                {p.mediaKind === 'image' ? (
                  <img
                    src={p.mediaUrl}
                    alt={p.mediaAlt ?? p.title}
                    className="aspect-[16/10] w-full object-cover"
                  />
                ) : (
                  <div className={`grid aspect-[16/10] w-full place-items-center text-[11px] ${palette.rail} ${palette.faintText}`}>
                    ▶ {p.mediaUrl}
                  </div>
                )}
              </div>
            ) : null}
            <h3 className={`font-display text-lg font-semibold leading-snug tracking-tight ${dark ? 'text-white' : 'text-ink'}`}>
              {p.title}
            </h3>
            {p.summary ? (
              <p className={`mt-2 text-sm leading-relaxed ${palette.mutedText}`}>{p.summary}</p>
            ) : null}
            {p.bullets && p.bullets.length ? (
              <ul className={`mt-3 flex-1 space-y-1.5 text-sm ${palette.mutedText}`}>
                {p.bullets.map((b, i) => (
                  <li key={i} className="flex gap-2">
                    <span className={`mt-1 h-1 w-1 shrink-0 rounded-full ${palette.accent}`} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            ) : null}
            {p.tags && p.tags.length ? (
              <div className="mt-3 flex flex-wrap gap-1">
                {p.tags.map((t) => (
                  <span
                    key={t}
                    className={`rounded border px-1.5 py-0.5 text-[10px] ${palette.cardBorder} ${palette.faintText} ${palette.shell}`}
                  >
                    {t}
                  </span>
                ))}
              </div>
            ) : null}
            {p.url ? (
              <a
                href={p.url}
                target="_blank"
                rel="noreferrer"
                className={`mt-4 inline-flex items-center gap-1 text-sm font-semibold ${dark ? 'text-violet-400 hover:text-violet-300' : 'text-violet-600 hover:text-violet-700'}`}
              >
                Learn More…
                <CaretRight size={12} weight="bold" />
              </a>
            ) : null}
          </article>
        ))}
      </div>
    </div>
  );
}

function ExperienceBlock({ values, ctx }: { values: ExperienceValues; ctx: CompassCtx }) {
  const { palette, dark } = ctx;
  if (!values.items.length) return null;
  return (
    <div>
      <SectionTitle label="Experience" title="Where I've shipped and what I learned." ctx={ctx} />
      <div className="space-y-6">
        {values.items.map((e) => (
          <div key={e.id} className={`rounded-xl border p-5 ${palette.cardBg} ${palette.cardBorder}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className={`font-display text-lg font-semibold tracking-tight ${dark ? 'text-white' : 'text-ink'}`}>
                {e.role}
              </h3>
              <span className={`font-mono text-[11px] uppercase tracking-[0.15em] ${palette.faintText}`}>
                {e.start} — {e.current ? 'Present' : e.end}
              </span>
            </div>
            <p className={`mt-1 text-sm ${palette.mutedText}`}>
              {e.company}
              {e.location ? ` · ${e.location}` : ''}
            </p>
            {e.summary ? (
              <p className={`mt-3 max-w-[70ch] text-sm leading-relaxed ${dark ? 'text-white' : 'text-ink'}`}>
                {e.summary}
              </p>
            ) : null}
            {e.highlights && e.highlights.length ? (
              <ul className={`mt-3 space-y-1.5 text-sm ${palette.mutedText}`}>
                {e.highlights.map((h, i) => (
                  <li key={i} className="flex gap-2">
                    <span className={`mt-1 h-1 w-1 shrink-0 rounded-full ${palette.accent}`} />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function EducationBlock({ values, ctx }: { values: EducationValues; ctx: CompassCtx }) {
  const { palette, dark } = ctx;
  if (!values.items.length) return null;
  return (
    <div>
      <SectionTitle label="Education" title="Where I studied the fundamentals." ctx={ctx} />
      <div className="space-y-4">
        {values.items.map((e) => (
          <div key={e.id} className={`rounded-xl border p-5 ${palette.cardBg} ${palette.cardBorder}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className={`font-display text-lg font-semibold tracking-tight ${dark ? 'text-white' : 'text-ink'}`}>
                {e.institution}
              </h3>
              <span className={`font-mono text-[11px] uppercase tracking-[0.15em] ${palette.faintText}`}>
                {e.startYear}
                {e.endYear ? ` – ${e.endYear}` : ''}
              </span>
            </div>
            <p className={`mt-1 text-sm ${palette.mutedText}`}>
              {e.credential}
              {e.gpa ? ` · GPA ${e.gpa}` : ''}
            </p>
            {e.note ? <p className={`mt-2 text-sm ${palette.mutedText}`}>{e.note}</p> : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function ContactBlock({ values, ctx }: { values: ContactValues; ctx: CompassCtx }) {
  const { palette, dark } = ctx;
  const linkClass = `flex items-center justify-between rounded-xl border px-4 py-3 text-sm font-medium transition-colors ${palette.cardBg} ${palette.cardBorder} ${dark ? 'text-white hover:bg-[#1B1B24]' : 'text-ink hover:bg-rail'}`;
  return (
    <div>
      <SectionTitle label="Contact" title="The best ways to reach out." ctx={ctx} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {values.email ? (
          <a href={`mailto:${values.email}`} className={linkClass}>
            <span className="inline-flex items-center gap-2">
              <EnvelopeSimple size={14} className={palette.mutedText} />
              {values.email}
            </span>
            <ArrowUpRight size={12} weight="bold" className={palette.faintText} />
          </a>
        ) : null}
        {values.phone ? (
          <a href={`tel:${values.phone.replace(/\s+/g, '')}`} className={linkClass}>
            <span className="inline-flex items-center gap-2">
              <Phone size={14} className={palette.mutedText} />
              {values.phone}
            </span>
            <ArrowUpRight size={12} weight="bold" className={palette.faintText} />
          </a>
        ) : null}
        {values.links.map((l) => {
          const meta = PLATFORM_META[l.platform] ?? PLATFORM_META.custom;
          const Icon = meta.icon;
          const href =
            l.platform === 'email' && !l.href.startsWith('mailto:') ? `mailto:${l.href}` : l.href;
          return (
            <a
              key={l.id}
              href={href}
              target="_blank"
              rel="noreferrer"
              className={linkClass}
            >
              <span className="inline-flex items-center gap-2">
                <Icon size={14} weight="regular" className={palette.mutedText} />
                {l.label || meta.label}
              </span>
              <ArrowUpRight size={12} weight="bold" className={palette.faintText} />
            </a>
          );
        })}
      </div>
      {values.location ? (
        <p className={`mt-6 text-sm ${palette.mutedText}`}>Based in {values.location}</p>
      ) : null}
      {values.allowInquiryForm ? (
        <div className={`mt-6 rounded-xl border border-dashed p-5 ${palette.cardBg} ${palette.cardBorder}`}>
          <p className={`text-sm font-semibold ${dark ? 'text-white' : 'text-ink'}`}>Send an inquiry</p>
          <p className={`mt-1 text-sm ${palette.mutedText}`}>
            A short inquiry form renders here on your live site.
          </p>
        </div>
      ) : null}
    </div>
  );
}
